import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import { Loader } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { setTotalConfigObjects, setDiscoveryCompleted } from '../../redux/migrationSlice';
import {
  calculateTotalConfigObjects,
  getSourceConfig,
  getProgressSteps,
  simulateProgressSteps,
} from '../../utils/migrationUtils';
import { formatApiError } from '../../utils/apiUtils';
import { fetchObjectTypeCounts, fetchDiscoveryStatus } from '../../redux/migrationThunks';
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  BUTTON_LABELS,
  TITLES,
  PROGRESS_MESSAGES,
} from '../../constants/stringConstants';
import ProgressIndicator from '../shared/ProgressIndicator';
import MigrationStep from './MigrationStep';
import PrerequisitesModal from './PrerequisitesModal';
import useStyles from './styles/index.styles';

function GettingStarted({
  sourceType,
  sourceID,
  onNavigate,
  onConnectionSaved,
  dataMigrationTabEnabled,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showPrerequisites, setShowPrerequisites] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState(null);
  const [discoveryProgress, setDiscoveryProgress] = useState(null);
  const { sourceStatus } = useSelector((state) => state.migration);
  const discoveryCompleted = !!sourceStatus?.discoveryCompleted;

  const config = getSourceConfig(sourceType);

  const handleDiscover = useCallback(async () => {
    if (discoveryCompleted) {
      setError(ERROR_MESSAGES.DISCOVERY_ALREADY_COMPLETED);
      setTimeout(() => setError(null), 3000);
      return;
    }

    setDiscovering(true);
    setError(null);

    try {
      if (!sourceID) {
        setError('Source not configured. Please Configure in Prerequisites Settings first.');
        setDiscovering(false);
        setDiscoveryProgress(null);
        return;
      }

      const steps = getProgressSteps(sourceType, 'discovery');
      await simulateProgressSteps(
        steps,
        (percentage, message) => {
          setDiscoveryProgress(message);
        },
        1000
      );

      const data = await dispatch(
        fetchObjectTypeCounts({
          sourceId: sourceID,
          sourceType,
        })
      ).unwrap();

      // Ensure data is an array before processing
      const dataArray = data?.data || data || [];
      const totalConfig = calculateTotalConfigObjects(dataArray);
      dispatch(setTotalConfigObjects(totalConfig));
      dispatch(setDiscoveryCompleted());

      // Refresh discovery status to get updated discoveryCompleted flag
      await dispatch(fetchDiscoveryStatus(sourceID)).unwrap();

      setDiscoveryProgress(PROGRESS_MESSAGES.DISCOVERY_COMPLETED);
      setTimeout(() => {
        setDiscoveryProgress(null);
        onNavigate(1);
      }, 1500);
    } catch (err) {
      setError(formatApiError(err));
      setDiscoveryProgress(null);
    } finally {
      setDiscovering(false);
    }
  }, [sourceType, sourceID, dispatch, onNavigate, discoveryCompleted]);

  if (sourceID === undefined) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  const migrationSteps = [
    {
      id: 'prerequisites',
      number: '1',
      title: 'Prerequisites (Initial Setup)',
      description:
        'Configure connection settings and user attribute mappings before starting migration.',
      action: () => setShowPrerequisites(true),
      buttonText: 'Settings',
      disabled: false,
      completed: false,
      showButton: true,
    },
    {
      id: 'discover-objects',
      number: '2',
      title: 'Discover Objects (Visibility)',
      description: `Connect to your ${config.name} environment and get visibility to different objects present. Once discovered you can migrate the objects to Saviynt.`,
      action: handleDiscover,
      buttonText: (() => {
        if (discovering) return BUTTON_LABELS.DISCOVERING;
        if (discoveryCompleted) return SUCCESS_MESSAGES.COMPLETED_CHECKMARK;
        return BUTTON_LABELS.DISCOVER;
      })(),
      disabled: discoveryCompleted || !sourceID,
      completed: discoveryCompleted,
      showButton: !discoveryCompleted,
    },
    {
      id: 'migrate-configuration',
      number: '3',
      title: TITLES.CONFIGURATION_MIGRATION,
      description: `Choose and migrate various configuration objects & applications from ${config.name}.`,
      action: () => {
        if (!discoveryCompleted) {
          setError(ERROR_MESSAGES.COMPLETE_DISCOVERY_FIRST_CONFIG);
          setTimeout(() => setError(null), 3000);
          return;
        }
        onNavigate(2);
      },
      buttonText: BUTTON_LABELS.MIGRATE_CONFIGURATION,
      disabled: !discoveryCompleted,
      completed: false,
      showButton: true,
    },
    {
      id: 'migrate-data',
      number: '4',
      title: TITLES.DATA_MIGRATION,
      description: `Build Identity Warehouse in Saviynt by migrating one time import of different data objects from ${config.name} to Saviynt.`,
      action: () => {
        if (!discoveryCompleted) {
          setError(ERROR_MESSAGES.COMPLETE_DISCOVERY_FIRST_DATA);
          setTimeout(() => setError(null), 3000);
          return;
        }
        onNavigate(3);
      },
      buttonText: BUTTON_LABELS.MIGRATE_DATA,
      disabled: !dataMigrationTabEnabled,
      completed: false,
      showButton: true,
    },
  ];

  return (
    <div className={classes.container}>
      {discoveryProgress && (
        <div className={classes.progressContainer}>
          <ProgressIndicator message={discoveryProgress} showProgress variant="info" />
        </div>
      )}

      {error && (
        <div className={classes.errorBox}>
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        </div>
      )}

      <PrerequisitesModal
        isOpen={showPrerequisites}
        onClose={() => setShowPrerequisites(false)}
        sourceID={sourceID}
        sourceType={sourceType}
        onConnectionSaved={onConnectionSaved}
      />
      <div className={classes.stepsContainer}>
        {migrationSteps.map((step, index) => (
          <MigrationStep
            key={step.id}
            step={step}
            index={index}
            totalSteps={migrationSteps.length}
          />
        ))}
      </div>
    </div>
  );
}

GettingStarted.propTypes = {
  sourceType: PropTypes.string.isRequired,
  sourceID: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  onConnectionSaved: PropTypes.func,
};

GettingStarted.defaultProps = {
  sourceID: undefined,
  onConnectionSaved: null,
};

export default GettingStarted;
